/**
 * Notification Badge Component
 *
 * Displays a notification bell icon with unread count badge.
 * Clicking opens the notification dropdown.
 * Uses shared NotificationContext for state synchronization.
 *
 * Requirements: 10.7
 * Fixes: Task 14.9.2 - Dashboard notification badge sync
 */

import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNotifications } from '../../contexts';

// Lazy: NotificationList statically pulls the aws-amplify-backed lib/notifications.
// The dropdown only renders when the bell is opened, so deferring it keeps the
// logged-out boot path free of the SDK.
const NotificationList = lazy(() =>
  import('./NotificationList').then((m) => ({ default: m.NotificationList }))
);

export function NotificationBadge() {
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className="relative p-2 text-bbx-dark-400 hover:text-bbx-dark-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label={`${unreadCount} unread notifications`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-bbx-neon-blue text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 bg-bbx-dark-800 rounded-lg shadow-lg border border-bbx-dark-700 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-bbx-dark-700">
            <h3 className="text-sm font-semibold text-bbx-dark-100">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="text-xs text-bbx-neon-blue hover:text-bbx-neon-blue/80 transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <Suspense fallback={<div className="px-4 py-6 text-center text-sm text-bbx-dark-400">Loading…</div>}>
            <NotificationList
              notifications={notifications}
              isLoading={isLoading}
              onMarkAsRead={markAsRead}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
